import React, { useContext } from "react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";

import { useHistory } from "react-router";
import { Link } from "react-router-dom";

import moment from "moment";

import Autocomplete from "@material-ui/lab/Autocomplete";

import { Alert } from "@material-ui/lab";

import Risk from "../Risk";
import { effectiveRiskLevel, WebTracker } from "../types";
import { resolutionTypeAdjective } from "../WebTrackerDetailsPage/TrackerResolution";
import {
  getFollowUpTasks,
  formatIncompleteFollowUpTasks,
} from "../WebTrackerDetailsPage/FollowUpTasks/FollowUpTasksTypes";
import SharedTableHead from "./SharedTableHead";
import DuplicateTrackerWarningIcon, {
  useDuplicateTrackerWarningBackgroundColor,
} from "./DuplicateTrackerWarningIcon";
import useFeatureFlags from "../../hooks/useFeatureFlag";
import useWTMURLs from "../useWTMURLs";
import TrackerReportTitle from "./ListTab/TrackerReportTitle";
import useWTMPageStyles from "./useWTMPageStyles";
import RiskSelect from "../RiskSelect";
import DownloadLatestReportButton from "../DownloadLatestReportButton";
import { EmptyState } from "./EmptyState";
import { OverviewDataContext } from "../OverviewDataContextProvider";

function WebTrackerOverviewTable() {
  const classes = useWTMPageStyles();
  const history = useHistory();
  const flags = useFeatureFlags();
  const duplicateTrackerWarningBackgroundColor = useDuplicateTrackerWarningBackgroundColor();
  const wtmURLs = useWTMURLs();

  const {
    columns,
    latestReport,
    latestReportLoading,
    latestReportError,
    searchStringDisplay,
    onSearchTrackers,
    sort,
    onSort,
    riskFilter,
    setRiskFilter,
    trackerReportRows,
    potentialDuplicateTrackerNames,
    trackerNameHovered,
    setTrackerNameHovered,
  } = useContext(OverviewDataContext);

  const isLoading = latestReportLoading;
  const isError = latestReportError;

  const handleRowMouseEnter = (webTracker: WebTracker) => {
    if (
      !flags("unifiedDestinationsUI") ||
      !potentialDuplicateTrackerNames.has(webTracker.destinationKey)
    ) {
      return;
    }

    setTrackerNameHovered(webTracker.destinationKey);
  };

  const handleRowMouseLeave = (webTracker: WebTracker) => {
    if (
      !flags("unifiedDestinationsUI") ||
      webTracker.destinationKey !== trackerNameHovered
    ) {
      return;
    }

    setTrackerNameHovered(null);
  };

  if (isError) {
    return (
      <Alert severity="error">
        <Typography variant="body1">
          Something went wrong. Try refreshing the page, or contact
          support@freshpaint.io for assistance.
        </Typography>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <div className={classes.loader}>
        <CircularProgress />
      </div>
    );
  }

  if (latestReport === undefined) {
    return <EmptyState />;
  }

  return (
    <Card>
      <CardHeader
        className={classes.cardHeader}
        title={<TrackerReportTitle report={latestReport.report} />}
      />
      <Divider />
      <CardContent className={classes.cardContent}>
        <nav className={classes.cardNav}>
          <Grid
            container
            spacing={1}
            justifyContent="space-between"
            alignItems="center"
          >
            <Grid item container xs={6} spacing={2}>
              <Grid item xs={8}>
                <Autocomplete
                  disableClearable
                  freeSolo
                  onInputChange={(_, value) => onSearchTrackers(value)}
                  options={(latestReport?.trackers ?? [])
                    .map((t) => t.name)
                    .sort()}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search..."
                      type="search"
                      value={searchStringDisplay}
                      variant="outlined"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={4}>
                <RiskSelect
                  selectedRiskValues={riskFilter}
                  onChange={setRiskFilter}
                />
              </Grid>
            </Grid>
            <Grid item xs={2} style={{ textAlign: "right" }}>
              <DownloadLatestReportButton />
            </Grid>
          </Grid>
        </nav>

        <TableContainer>
          <Table>
            <SharedTableHead columns={columns} sort={sort} onSort={onSort} />

            <TableBody>
              {trackerReportRows.map(
                ({
                  onNumPages,
                  firstDetectedAt,
                  lastDetectedAt,
                  webTracker,
                }) => (
                  <TableRow
                    key={`tracker-row-${webTracker.name}`}
                    onMouseEnter={() => handleRowMouseEnter(webTracker)}
                    onMouseLeave={() => handleRowMouseLeave(webTracker)}
                    style={{
                      background: duplicateTrackerWarningBackgroundColor(
                        webTracker.destinationKey,
                        trackerNameHovered
                      ),
                    }}
                  >
                    <TableCell>
                      <Link to={wtmURLs.urlForTrackerDetail(webTracker)}>
                        {webTracker.name}
                      </Link>

                      <DuplicateTrackerWarningIcon
                        potentialDuplicateTrackerNames={
                          potentialDuplicateTrackerNames
                        }
                        destinationKey={webTracker.destinationKey}
                      />
                    </TableCell>
                    <TableCell align="center">{onNumPages}</TableCell>
                    <TableCell align="center">
                      {resolutionTypeAdjective(
                        webTracker.resolution?.resolutionType ?? "unresolved"
                      )}
                    </TableCell>
                    <TableCell align="left">
                      {formatIncompleteFollowUpTasks(
                        getFollowUpTasks(
                          webTracker.resolution?.resolutionType ?? "unresolved",
                          webTracker
                        )
                      )}
                    </TableCell>
                    <TableCell align="center" style={{ minWidth: "12em" }}>
                      <Risk
                        description={webTracker.description}
                        riskLevel={effectiveRiskLevel(webTracker)}
                      />
                    </TableCell>
                    <TableCell>{moment(firstDetectedAt).fromNow()}</TableCell>
                    <TableCell>
                      {lastDetectedAt && moment(lastDetectedAt).fromNow()}
                    </TableCell>
                    <TableCell>
                      <Button
                        fullWidth
                        color="primary"
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          history.push(wtmURLs.urlForTrackerDetail(webTracker))
                        }
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

export default WebTrackerOverviewTable;
